import { useCallback, useEffect, useState } from "react";
import FullNameInput from "./Inputs/FullNameInput";
import EmailInput from "./Inputs/EmailInput";
import PhoneInput from "react-phone-input-2";
import AddressInput from "./Inputs/AddressInput";
import IdentityDocumentTypeSelect from "./Inputs/IdentityDocumentTypeSelect";
import IdentityDocumentInput from "./Inputs/IdentityDocumentInput";
import {
    selectIsSaving,
    selectIsEditingMode,
    selectUser,
    setIsSaving,
    updateUser,
    setIsEditingMode,
} from "../userSlice";
import { useDispatch, useSelector } from "react-redux";
import { pushAlert } from "../../alerts/alertSlice";

import style from "../../../sass/pages/profile/profile.module.scss";
import { validateUser } from "../../../validators/validateUser";
import SenderCityInput from "./Inputs/SenderCityInput";
import { useTranslation } from "react-i18next";
import LanguageInput from "./Inputs/LanguageInput";
import SenderTownInput from "./Inputs/SenderTownInput";
import SenderDistrictInput from "./Inputs/SenderDistrictInput";

const IndividualInputs = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const user = useSelector(selectUser);
    const isEditing = useSelector(selectIsEditingMode);
    const isSaving = useSelector(selectIsSaving);

    const [fullName, setFullName] = useState("");
    const [email, setEmail] = useState("");
    const [phoneNumber, setPhoneNumber] = useState("");
    const [senderCityCode, setSenderCityCode] = useState("");
    const [senderTownID, setSenderTownID] = useState("");
    const [initialSenderDistrict, setInitialSenderDistrict] = useState("");
    const [senderDistictID, setSenderDistrictID] = useState("");
    const [address, setAddress] = useState("");
    const [identityDocument, setIdentityDocument] = useState("");
    const [identityDocumentNumber, setIdentityDocumentNumber] = useState("");
    const [language, setLanguage] = useState("tr");

    const getData = useCallback(() => {
        return {
            language,
            "user-type": user["user-type"],
            "full-name": fullName,
            email,
            "phone-number": phoneNumber,
            "sender-city-code": senderCityCode,
            "sender-town-id": +senderTownID,
            "sender-district-id": +senderDistictID,
            address,
            "identity-document-type": identityDocument,
            "identity-document-number": identityDocumentNumber,
        };
    }, [
        address,
        email,
        fullName,
        identityDocument,
        identityDocumentNumber,
        language,
        phoneNumber,
        senderCityCode,
        senderDistictID,
        senderTownID,
        user,
    ]);

    useEffect(() => {
        if (user) {
            setLanguage(user.language ?? "tr");
            setFullName(user["full-name"] ?? "");
            setEmail(user.email ?? "");
            setPhoneNumber(user["phone-number"] ?? "");
            setAddress(user.address ?? "");
            setSenderCityCode(user["sender-city-code"] ?? "");
            setSenderTownID(user["sender-town-id"] ?? "");
            setInitialSenderDistrict(user["sender-district-id"] ?? "");
            setIdentityDocument(user["identity-document-type"] ?? "");
            setIdentityDocumentNumber(user["identity-document-number"] ?? "");
        }
    }, [user]);

    useEffect(() => {
        if(initialSenderDistrict)
            setSenderDistrictID(initialSenderDistrict);
    }, [initialSenderDistrict])

    useEffect(() => {
        if (!isSaving || !user["user-type"]) {
            return;
        }

        let errors = validateUser(getData());
        console.log("errors:", errors);
        if (errors.length > 0) {
            dispatch(setIsSaving(false));
            dispatch(setIsEditingMode(true));

            errors.map((error) =>
                dispatch(
                    pushAlert({
                        message: t(error),
                        delay: 2000,
                        type: "danger",
                    })
                )
            );

            return;
        }

        dispatch(
            updateUser({
                data: getData(),
            })
        );
        dispatch(setIsSaving(false));
        dispatch(setIsEditingMode(false));
    }, [dispatch, getData, isSaving, user]);

    return (
        <div className="flex flex-col gap-4">
            <LanguageInput
                state={language}
                setState={setLanguage}
                enabled={isEditing}
            />
            <FullNameInput
                state={fullName}
                setState={setFullName}
                enabled={isEditing}
            />
            <EmailInput state={email} setState={setEmail} enabled={isEditing} />
            <div className="grid grid-cols-8 xs:grid-cols-12 items-center">
                <span className="col-span-4 text-xs text-left font-bold">
                    {t("user.inputs.phone.title")}
                </span>
                <PhoneInput
                    disabled={!isEditing}
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e)}
                    country={"tr"}
                    inputClass={`${style.input} col-span-12 xs:col-span-8`}
                    specialLabel=""
                    className="col-span-8"
                />
            </div>
            <SenderCityInput
                state={senderCityCode}
                setState={setSenderCityCode}
                enabled={isEditing}
            />
            <SenderTownInput
                state={senderTownID}
                setState={setSenderTownID}
                enabled={isEditing}
                senderCityCode={senderCityCode}
            />
            <SenderDistrictInput
                state={senderDistictID}
                setState={setSenderDistrictID}
                enabled={isEditing}
                senderTownID={senderTownID}
                initialID={initialSenderDistrict}
            />
            <AddressInput
                state={address}
                setState={setAddress}
                enabled={isEditing}
            />
            <IdentityDocumentTypeSelect
                state={identityDocument}
                setState={setIdentityDocument}
                enabled={isEditing}
            />
            <IdentityDocumentInput
                state={identityDocumentNumber}
                setState={setIdentityDocumentNumber}
                enabled={isEditing}
            />
        </div>
    );
};

export default IndividualInputs;
